/**
 * ADR-165 — the pure logic behind the Overlapping Position Legs report and the
 * contract deletion dialog, kept out of the components so it can be tested in
 * the node jest environment.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const fmtDay = (ymd) => (ymd ? dayjs.utc(ymd).format('DD/MM/YYYY') : 'Present');
export const legRange = (leg) => `${fmtDay(leg.startDate)} – ${fmtDay(leg.endDate)}`;

export const KIND_LABELS = {
  OPEN_OPEN: 'Two open',
  CLOSED_OVERLAP: 'Closed overlap'
};

export const ACTION_LABELS = {
  CLOSE: 'Close older',
  REANCHOR: 'Move older back',
  REALIGN: 'Move newer forward',
  TRIM: 'Trim one-day overlap',
  SAME_START: 'Manual: same start',
  GRADE_AFTER_CLOSE: 'Manual: grade after close'
};

/** Why a row is manual (null for rows the repair can apply). */
export const rowReason = (row) => (row.automatic ? null : (row.manualReason || row.realignReason || null));

/** Client-side filters: kind, action, reason, the individual (id from Search individuals). */
export const filterRows = (rows, { kind = '', action = '', reason = '', regionId = '', employeeId = '' } = {}) =>
  (rows || []).filter((r) =>
    (!kind || r.kind === kind)
    && (!action || r.action === action)
    && (!reason || rowReason(r) === reason)
    && (!regionId || r.regionId === regionId)
    && (!employeeId || r.employeeId === employeeId));

/** The planned change, in words — the confirm dialog of "Repair this person". */
export const describePlannedChange = (row) => {
  const pc = row.plannedChange;
  if (!pc || !row.automatic) return [];
  const older = row.legA.position || 'Older position';
  if (row.action === 'CLOSE') {
    return [`${older} (from ${fmtDay(row.legA.startDate)}): end → ${fmtDay(pc.newEndDate)}`];
  }
  if (row.action === 'REANCHOR') {
    return [`${older}: ${fmtDay(row.legA.startDate)} – Present → ${fmtDay(pc.newStartDate)} – ${fmtDay(pc.newEndDate)} (and its grade)`];
  }
  if (row.action === 'REALIGN' || row.action === 'TRIM') {
    return (pc.operations || []).map((op) => {
      const who = op.table === 'position'
        ? (op.id === row.legA.id ? `${older} (older)` : `${row.legB.position || 'Newer position'} (newer)`)
        : 'Grade';
      const isStart = Boolean(op.to.startDate);
      const from = isStart ? op.from.startDate : op.from.endDate;
      const to = isStart ? op.to.startDate : op.to.endDate;
      return `${who}: ${isStart ? 'start' : 'end'} ${fmtDay(from)} → ${fmtDay(to)}`;
    });
  }
  return [];
};

/**
 * The contracts worth showing on a row: those that cover any day of the
 * overlap, primary first. A person with a dozen yearly renewals would
 * otherwise fill the row; the rest go behind "+N more".
 */
export const relevantContracts = (row) => {
  const all = row.contracts || [];
  const from = row.overlap?.from || null;
  const to = row.overlap?.to || null;
  const covers = (c) => (!to || c.startDate <= to) && (!c.endDate || !from || c.endDate >= from);
  let shown = all.filter(covers);
  if (!shown.length) {
    const primary = all.find((c) => c.primary);
    shown = primary ? [primary] : all.slice(-1);
  }
  shown = [...shown].sort((a, b) => Number(b.primary) - Number(a.primary) || String(a.startDate).localeCompare(String(b.startDate)));
  return { shown, hidden: all.filter((c) => !shown.includes(c)) };
};

export const contractLabel = (c) => `${c.type || 'Contract'}${c.primary ? ' (primary)' : ''}: ${fmtDay(c.startDate)} – ${fmtDay(c.endDate)}`;

/** Rows per "Load More", as on the Ingestion Sync Runs list. */
export const PAGE_SIZE = 50;

/**
 * ADR-165 A5 — the delete button is enabled only when nothing blocks the
 * deletion and the operator has retyped the contract's start date.
 */
export const deletionConfirmText = (preview) => (preview ? fmtDay(preview.episode.startDate) : null);
export const canConfirmDeletion = (preview, typed) =>
  Boolean(preview) && preview.canDelete === true && String(typed || '').trim() === deletionConfirmText(preview);
